import React, { useState, useEffect } from "react";
import "../Zone/Zone.css";
import "../Overview/Overview.css";
import { fetchDevicesForLocation } from "../../Services/locationEntries";

function SnapPill({ variant, children }) {
  return <span className={`snap-pill ${variant}`}>{children}</span>;
}

function formatTakeoverTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86400000);
  const dayAfterStart = new Date(tomorrowStart.getTime() + 86400000);
  const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d >= todayStart && d < tomorrowStart) return `Today, ${timeStr}`;
  if (d >= tomorrowStart && d < dayAfterStart) return `Tomorrow, ${timeStr}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + `, ${timeStr}`;
}

function FeedFallback() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", gap: "8px" }}>
      <svg viewBox="0 0 24 24" width="28" height="28" style={{ stroke: "var(--t3)", fill: "none", strokeWidth: 1.5 }}>
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
      <span style={{ fontSize: "var(--text-base)", color: "var(--t3)" }}>No live feeds available</span>
    </div>
  );
}

function Location({ className, location, zone, ward, onBack, onComplaintsClick, onProceduresClick }) {
  const [feeds, setFeeds] = useState(null);

  useEffect(() => {
    if (!location?.documentId) return;
    setFeeds(null);
    fetchDevicesForLocation(location.documentId).then(setFeeds).catch(() => setFeeds([]));
  }, [location?.documentId]);

  if (!location) return null;

  const taken = location.takeoverTime != null;
  const state = location.operationalStatus?.state || "Inactive";
  const sc = taken ? "ok" : state === "Active" ? "warn" : "bad";
  const scLabel = { ok: "Taken Over", warn: "Active", bad: "Inactive" }[sc];

  const open = location.totalOpenCompliant ?? 0;
  const closed = location.totalClosedCompliant ?? 0;
  const review = location.totalUnderReviewCompliant ?? 0;
  const compTotal = open + closed + review;

  const procTotal = location.totalCompletedProcedure ?? 0;

  const compBars = [
    { value: open, color: "#7a3030" },
    { value: closed, color: "#3d7a50" },
    { value: review, color: "#7a6030" },
  ].filter((b) => b.value > 0);
  const compBarTotal = compBars.reduce((s, b) => s + b.value, 0) || 1;

  return (
    <div className={className} data-cy="location-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">
            Chennai › Zone {zone?.id} › Ward {ward?.id}
          </div>
          <div className="subhdr-title">{location.location || location.PCTNo}</div>
          {!taken && location.estimatedTakeoverTime && (
            <div style={{ fontSize: "var(--text-sm)", color: "var(--t3)", marginTop: "2px" }}>
              ETA {formatTakeoverTime(location.estimatedTakeoverTime)}
            </div>
          )}
        </div>
        <span className={`pill ${sc}`}>
          <span className="dot" />
          {scLabel}
        </span>
      </div>
      <div className="divider" />
      <div className="scroll">
        <div style={{ padding: "6px 20px 10px", fontSize: "var(--text-sm)", color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".04em" }}>
          {location.PCTNo}
        </div>

        {/* Live Feeds */}
        <div className="sec-hdr">
          <div className="t-section">Live Feeds</div>
        </div>
        {feeds === null ? (
          <div style={{ height: "56px", margin: "0 20px 10px", background: "var(--surface2)", borderRadius: "6px" }} />
        ) : feeds.length === 0 ? (
          <FeedFallback />
        ) : (
          feeds.map((device, i) => (
            <div key={i} style={{ position: "relative", paddingTop: "56.25%", margin: "0 20px 10px" }}>
              <iframe
                title={`feed-${i}`}
                src={device.stream}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", borderRadius: "6px" }}
              />
            </div>
          ))
        )}

        <div className="sec-hdr">
          <div className="t-section">Operations</div>
        </div>

        {/* Complaints */}
        <div className="snap-row" onClick={onComplaintsClick} data-cy="snap-row-complaints">
          <div className="snap-icon">
            <svg viewBox="0 0 24 24">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div className="snap-body">
            <div className="snap-name">Complaints — <strong>{compTotal}</strong></div>
            <div className="snap-pills">
              <SnapPill variant="neutral">{compTotal} total</SnapPill>
              {open > 0 && <SnapPill variant="bad">{open} open</SnapPill>}
              {review > 0 && <SnapPill variant="warn">{review} review</SnapPill>}
              {closed > 0 && <SnapPill variant="ok">{closed} closed</SnapPill>}
            </div>
            {compTotal > 0 && (
              <div style={{ display: "flex", height: "3px", marginTop: "8px", gap: "1px" }}>
                {compBars.map((b, i) => (
                  <div key={i} style={{ flex: b.value / compBarTotal, background: b.color, height: "100%" }} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Procedures */}
        <div className="snap-row" onClick={onProceduresClick} data-cy="snap-row-procedures">
          <div className="snap-icon">
            <svg viewBox="0 0 24 24">
              <polyline points="9,11 12,14 22,4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <div className="snap-body">
            <div className="snap-name">Procedures — <strong>{procTotal}</strong> completed</div>
            <div className="snap-pills">
              <SnapPill variant="neutral">{procTotal}</SnapPill>
            </div>
          </div>
        </div>

        <div style={{ height: "20px" }} />
      </div>
    </div>
  );
}

export default Location;
